import React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ArrowUpRight, ArrowDownRight, Package } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useInventoryStore } from '../../../../stores/appInventoryStore';
import {
  useLoadInventoryCurrentValue,
  useLoadInventoryCountsHistory,
} from '../../../../utils/dataLoadingUtils';
import { eur } from '../../../../components/orderhistory/utils';
import { fmtBase } from '../../../../utils/reports/shared';
import { relativeDaysLabel } from '../../../../utils/dateUtils';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import { SectionCard, EmptyState, EmptyCard, Kpi, ReportLoading, ReportTable, ReportColumn, useChartTheme } from './common';
import {
  computeInventoryReport,
  InventoryMover,
} from '../../../../utils/reports/inventoryReport';

/** Line colours for the per-group trend series (the total uses the brand green). */
const SERIES_COLORS = ['#2E90FA', '#F79009', '#9E77ED', '#EE46BC', '#12B76A', '#F04438'];

/** Movers table (increases or decreases between a group's two latest sessions). */
const MoversTable: React.FC<{ rows: InventoryMover[]; t: TFunction }> = ({ rows, t }) => {
  const columns: ReportColumn<InventoryMover>[] = [
    { key: 'product', header: t('reports.inventory.movers.product'), render: (r) => <span className="font-medium">{r.productName}</span> },
    { key: 'group', header: t('reports.inventory.movers.group'), render: (r) => <span className="text-muted dark:text-muted-dark">{r.groupName || t('reports.inventory.ungrouped')}</span> },
    { key: 'before', header: t('reports.inventory.movers.before'), align: 'right', render: (r) => <span className="text-muted dark:text-muted-dark">{eur(r.beforeValue)}</span> },
    { key: 'after', header: t('reports.inventory.movers.after'), align: 'right', render: (r) => eur(r.afterValue) },
    {
      key: 'change',
      header: t('reports.inventory.movers.change'),
      align: 'right',
      render: (r) => {
        const up = r.deltaValue > 0;
        return (
          <span className={`inline-flex items-center gap-0.5 font-medium ${up ? 'text-textGreen dark:text-textGreen-dark' : 'text-textRed dark:text-textRed-dark'}`}>
            {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {`${up ? '+' : '−'}${eur(Math.abs(r.deltaValue))}`}
          </span>
        );
      },
    },
  ];
  return <ReportTable columns={columns} rows={rows} rowKey={(r) => r.key} />;
};

const InventoryReport: React.FC = () => {
  const { t } = useTranslation();
  const theme = useChartTheme();

  // Both load only for the selected restaurant (ReportsGate guarantees a single one).
  useLoadInventoryCurrentValue();
  useLoadInventoryCountsHistory();

  const currentValue = useInventoryStore((s) => s.currentValue);
  const countsHistory = useInventoryStore((s) => s.countsHistory);
  const isLoadingCounts = useInventoryStore((s) => s.isLoadingCounts);
  const isLoadingCurrentValue = useInventoryStore((s) => s.isLoadingCurrentValue);

  const report = cachedReportValue('inventory.report', [countsHistory, currentValue], () =>
    computeInventoryReport({ countsHistory, currentValue, now: Date.now() })
  );

  // While the inventory stores are still loading and empty, show a spinner instead
  // of flashing the "no counts" empty state.
  if ((isLoadingCounts && countsHistory.length === 0) || (isLoadingCurrentValue && !currentValue)) {
    return <ReportLoading />;
  }

  if (!report.hasCounts) {
    return <EmptyCard message={t('reports.inventory.empty')} icon={Package} />;
  }

  const sectionColumns: ReportColumn<typeof report.sections[number]>[] = [
    { key: 'section', header: t('reports.inventory.current.section'), render: (s) => <span className="font-medium">{s.sectionName}</span> },
    { key: 'group', header: t('reports.inventory.current.group'), render: (s) => <span className="text-muted dark:text-muted-dark">{s.groupName || t('reports.inventory.ungrouped')}</span> },
    { key: 'value', header: t('reports.inventory.current.value'), align: 'right', render: (s) => eur(s.value) },
    { key: 'lastCounted', header: t('reports.inventory.current.lastCounted'), align: 'right', render: (s) => <span className="text-muted dark:text-muted-dark">{relativeDaysLabel(t, s.daysAgo)}</span> },
  ];

  return (
    <div className="mt-4">
      {/* Current value + staleness */}
      <div className="mb-6">
        <Kpi label={t('reports.inventory.current.totalValue')} value={eur(report.totalCurrentValue)} />
      </div>
      <SectionCard
        title={t('reports.inventory.current.title')}
        subtitle={t('reports.inventory.current.subtitle')}
      >
        {report.sections.length === 0 ? (
          <EmptyState message={t('reports.inventory.current.empty')} />
        ) : (
          <ReportTable columns={sectionColumns} rows={report.sections} rowKey={(s) => `${s.groupName ?? ''}-${s.sectionId ?? s.sectionName}`} />
        )}
      </SectionCard>

      {/* Value trend across sessions */}
      <SectionCard
        title={t('reports.inventory.trend.title')}
        subtitle={t('reports.inventory.trend.subtitle')}
      >
        {report.hasTrend ? (
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={report.trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="invTotalGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3DCB9A" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#3DCB9A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme.gridLine} />
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: theme.axisTick, fontSize: 11 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: theme.axisTick, fontSize: 11 }}
                  width={70}
                  tickFormatter={(value: number) => eur(value)}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [eur(value), name]}
                  cursor={{ stroke: theme.gridLine }}
                  contentStyle={{ backgroundColor: theme.tooltipBg, borderColor: theme.tooltipBorder, color: theme.tooltipText }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="total"
                  name={t('reports.inventory.trend.totalSeries')}
                  stroke="#18B081"
                  strokeWidth={2}
                  fill="url(#invTotalGradient)"
                  dot={false}
                />
                {report.trendSeries.map((series, i) => (
                  <Area
                    key={series.dataKey}
                    type="monotone"
                    dataKey={series.dataKey}
                    name={series.label || t('reports.inventory.ungrouped')}
                    stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                    strokeWidth={1.5}
                    fillOpacity={0}
                    dot={false}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState message={t('reports.inventory.trend.notEnough')} />
        )}
      </SectionCard>

      {/* Biggest-value items currently held */}
      <SectionCard
        title={t('reports.inventory.held.title')}
        subtitle={t('reports.inventory.held.subtitle')}
      >
        {report.heldItems.length === 0 ? (
          <EmptyState message={t('reports.inventory.held.empty')} />
        ) : (
          <ReportTable
            columns={[
              { key: 'product', header: t('reports.inventory.held.product'), render: (item) => <span className="font-medium">{item.productName}</span> },
              { key: 'group', header: t('reports.inventory.held.group'), render: (item) => <span className="text-muted dark:text-muted-dark">{item.groupName || t('reports.inventory.ungrouped')}</span> },
              { key: 'quantity', header: t('reports.inventory.held.quantity'), align: 'right', render: (item) => <span className="text-muted dark:text-muted-dark">{fmtBase(item.quantity, item.unit)}</span> },
              { key: 'value', header: t('reports.inventory.held.value'), align: 'right', render: (item) => <span className="font-medium">{eur(item.value)}</span> },
            ]}
            rows={report.heldItems}
            rowKey={(item) => item.key}
          />
        )}
      </SectionCard>

      {/* Movers between the two most recent sessions per group */}
      <SectionCard
        title={t('reports.inventory.movers.title')}
        subtitle={t('reports.inventory.movers.subtitle')}
      >
        {!report.hasMovers ? (
          <EmptyState message={t('reports.inventory.movers.notEnough')} />
        ) : (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-2">
                {t('reports.inventory.movers.risers')}
              </h3>
              {report.risers.length === 0 ? (
                <EmptyState message={t('reports.inventory.movers.noRisers')} />
              ) : (
                <MoversTable rows={report.risers} t={t} />
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-textPrimary dark:text-textPrimary-dark mb-2">
                {t('reports.inventory.movers.fallers')}
              </h3>
              {report.fallers.length === 0 ? (
                <EmptyState message={t('reports.inventory.movers.noFallers')} />
              ) : (
                <MoversTable rows={report.fallers} t={t} />
              )}
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default InventoryReport;
